"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useIsScrolling = void 0;
var _web = require("@react-hookz/web");
var _react = require("react");
var _useLiveRef = require("../useLiveRef/useLiveRef");
/**
 * Provides a ref to attach to an element. Will also return `isScrolling` = true if the element
 * has scrolled within the last `checkDelay` milliseconds.
 *
 * This was loosely based on useScrolling from https://github.com/streamich/react-use but has been
 * optimized to cut out a few extra renders.
 */
const useIsScrolling = function () {
  let {
    checkDelay = 150
  } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  const [isScrolling, setIsScrolling] = (0, _react.useState)(false);
  const latestIsScrollingRef = (0, _useLiveRef.useLiveRef)(isScrolling);
  const elementRef = (0, _react.useRef)(null);
  const isMounted = (0, _web.useIsMounted)();

  // Tracks the latest timeout id.
  const latestTimerRef = (0, _react.useRef)(0);
  const handleScrollEnd = (0, _react.useCallback)(() => {
    // Avoid re-renders that don't do anything...
    if (isMounted() && latestIsScrollingRef.current) {
      setIsScrolling(false);
    }
  }, [isMounted, latestIsScrollingRef]);
  const handleScroll = (0, _react.useCallback)(() => {
    // This check avoids re-renders on React 16 which doesn't no-op state setters that are
    // equivalent.
    if (!latestIsScrollingRef.current) {
      setIsScrolling(true);
    }
    clearTimeout(latestTimerRef.current);
    latestTimerRef.current = window.setTimeout(handleScrollEnd, checkDelay);
  }, [checkDelay, handleScrollEnd, latestIsScrollingRef]);
  const removeScrollHandler = (0, _react.useCallback)(element => {
    element.removeEventListener("scroll", handleScroll);
    setIsScrolling(false);
  }, [handleScroll]);
  const attachScrollHandler = (0, _react.useCallback)(element => {
    element.addEventListener("scroll", handleScroll, {
      passive: true
    });
  }, [handleScroll]);
  const previousCheckDelay = (0, _web.usePrevious)(checkDelay);
  (0, _react.useEffect)(() => {
    // If our delay changed and we're in the middle of scrolling, restart the timer w/ the new
    // delay.
    if (previousCheckDelay && checkDelay !== previousCheckDelay && latestIsScrollingRef.current) {
      handleScroll();
    }
  }, [checkDelay, handleScroll, latestIsScrollingRef, previousCheckDelay]);
  return {
    isScrolling,
    ref: (0, _react.useCallback)(element => {
      if (element) {
        elementRef.current = element;
        attachScrollHandler(element);
      } else if (elementRef.current) {
        // This means the hook is unmounting.
        removeScrollHandler(elementRef.current);
        handleScrollEnd();
        clearTimeout(latestTimerRef.current);
      }
    }, [attachScrollHandler, handleScrollEnd, removeScrollHandler])
  };
};
exports.useIsScrolling = useIsScrolling;
//# sourceMappingURL=useIsScrolling.js.map